import { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://ticobaterias.com/api';

type Tipo = 'whatsapp' | 'telefono' | 'cotizacion';

type Diagnostico = {
  etiqueta_activa: boolean;
  libreria_ga4: boolean;
  credenciales: boolean;
  correo_cuenta_servicio: string | null;
  propiedad: boolean;
  estadisticas_listas: boolean;
};

type Formulario = {
  activo: boolean;
  tag_id: string;
  conv_whatsapp: string;
  conv_whatsapp_activo: boolean;
  conv_telefono: string;
  conv_telefono_activo: boolean;
  conv_cotizacion: string;
  conv_cotizacion_activo: boolean;
  ga4_property_id: string;
};

type Config = Formulario & {
  actualizado_en: string | null;
  diagnostico: Diagnostico;
};

type Estadisticas = {
  listo: boolean;
  error?: string;
  dias?: number;
  generado_en?: string;
  totales?: { usuarios: number; sesiones: number; vistas: number; duracion_promedio_seg: number };
  conversiones?: Record<Tipo, number>;
  diario?: { fecha: string; usuarios: number; sesiones: number }[];
  canales?: { nombre: string; sesiones: number }[];
  paginas?: { ruta: string; vistas: number }[];
  dispositivos?: { nombre: string; usuarios: number }[];
  ciudades?: { nombre: string; usuarios: number }[];
};

type Fila = { nombre: string; valor: number };

const FORM_VACIO: Formulario = {
  activo: false,
  tag_id: '',
  conv_whatsapp: '',
  conv_whatsapp_activo: true,
  conv_telefono: '',
  conv_telefono_activo: true,
  conv_cotizacion: '',
  conv_cotizacion_activo: true,
  ga4_property_id: '',
};

const CONVERSIONES: { tipo: Tipo; icono: string; titulo: string; ayuda: string }[] = [
  { tipo: 'whatsapp', icono: '💬', titulo: 'Clic en WhatsApp', ayuda: 'Cualquier botón de WhatsApp del sitio, incluido el del chat.' },
  { tipo: 'telefono', icono: '📞', titulo: 'Clic en teléfono', ayuda: 'Botones de llamar (en celular abren el marcador).' },
  { tipo: 'cotizacion', icono: '🤖', titulo: 'Cotización del agente', ayuda: 'Cuando TicoBaterín le muestra precios a un cliente.' },
];

const CANALES: Record<string, string> = {
  'Organic Search': 'Google orgánico',
  'Paid Search': 'Google Ads',
  'Cross-network': 'Google Ads (varias redes)',
  Direct: 'Directo',
  'Organic Social': 'Redes sociales',
  'Paid Social': 'Redes sociales (pagado)',
  Referral: 'Otros sitios',
  'Organic Video': 'Video',
  Display: 'Display',
  Email: 'Correo',
  'Paid Other': 'Otros pagados',
  Unassigned: 'Sin asignar',
};

const DISPOSITIVOS: Record<string, string> = { desktop: 'Computadora', mobile: 'Celular', tablet: 'Tablet' };

const PAGINAS: Record<string, string> = {
  '/': 'Inicio',
  '/catalogo': 'Catálogo',
  '/contacto': 'Contacto',
  '/distribuidores': 'Distribuidores',
  '/tecnico': 'Técnico',
};

const traducir = (mapa: Record<string, string>, valor: string) =>
  valor === '(not set)' ? 'Sin dato' : mapa[valor] || valor;

const numero = (n: number) => n.toLocaleString('es-CR');

const duracion = (seg: number) => `${Math.floor(seg / 60)}:${String(seg % 60).padStart(2, '0')} min`;

// Las fechas sin zona horaria vienen del backend en UTC; las que traen zona se usan tal cual
const fecha = (s: string | null | undefined) =>
  s ? new Date(/(Z|[+-]\d{2}:\d{2})$/.test(s) ? s : s + 'Z').toLocaleString('es-CR') : 'nunca';

// ---------- piezas visuales ----------

function Interruptor({ activo, onChange, etiqueta }: { activo: boolean; onChange: (v: boolean) => void; etiqueta?: string }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!activo)}
      aria-pressed={activo}
      aria-label={etiqueta}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${activo ? 'bg-green-500' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${activo ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function Estado({ ok, texto }: { ok: boolean; texto: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
      {ok ? '✅' : '⏸️'} {texto}
    </span>
  );
}

function Kpi({ titulo, valor, detalle }: { titulo: string; valor: string; detalle?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="text-gray-500 text-xs font-medium">{titulo}</div>
      <div className="text-2xl font-bold text-azul mt-1">{valor}</div>
      {detalle && <div className="text-xs text-gray-400 mt-1">{detalle}</div>}
    </div>
  );
}

function Barras({ titulo, filas, vacio = 'Sin datos todavía' }: { titulo: string; filas: Fila[]; vacio?: string }) {
  const max = Math.max(1, ...filas.map(f => f.valor));
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <h3 className="font-bold text-azul mb-4">{titulo}</h3>
      {filas.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-4">{vacio}</div>
      ) : (
        <div className="space-y-3">
          {filas.map(f => (
            <div key={f.nombre}>
              <div className="flex justify-between text-sm mb-1 gap-3">
                <span className="text-gray-700 truncate">{f.nombre}</span>
                <span className="font-bold text-azul shrink-0">{numero(f.valor)}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-azul h-2 rounded-full" style={{ width: `${(f.valor / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GraficoDiario({ datos }: { datos: { fecha: string; usuarios: number }[] }) {
  if (datos.length === 0) return null;
  const max = Math.max(1, ...datos.map(d => d.usuarios));
  const ancho = 1000;
  const alto = 200;
  const paso = ancho / datos.length;
  const corto = (f: string) => {
    const [, m, d] = f.split('-');
    return `${d}/${m}`;
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex justify-between items-baseline mb-4">
        <h3 className="font-bold text-azul">Visitantes por día</h3>
        <span className="text-xs text-gray-400">Máximo: {numero(max)}</span>
      </div>
      <svg viewBox={`0 0 ${ancho} ${alto}`} preserveAspectRatio="none" className="w-full h-48" role="img" aria-label="Visitantes por día">
        {datos.map((d, i) => {
          const h = (d.usuarios / max) * (alto - 10);
          return (
            <rect key={d.fecha} x={i * paso + paso * 0.15} y={alto - h} width={paso * 0.7} height={Math.max(h, d.usuarios > 0 ? 2 : 0)} rx={2} className="fill-current text-azul">
              <title>{`${corto(d.fecha)}: ${d.usuarios} visitantes`}</title>
            </rect>
          );
        })}
        <line x1={0} y1={alto} x2={ancho} y2={alto} stroke="#e5e7eb" strokeWidth={2} />
      </svg>
      <div className="flex justify-between text-xs text-gray-400 mt-2">
        <span>{corto(datos[0].fecha)}</span>
        <span>{corto(datos[datos.length - 1].fecha)}</span>
      </div>
    </div>
  );
}

// ---------- pagina ----------

export default function GoogleMedicion() {
  const [pestana, setPestana] = useState<'estadisticas' | 'configuracion'>('configuracion');
  const [config, setConfig] = useState<Config | null>(null);
  const [form, setForm] = useState<Formulario>(FORM_VACIO);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const [dias, setDias] = useState(28);
  const [stats, setStats] = useState<Estadisticas | null>(null);
  const [cargandoStats, setCargandoStats] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const aplicarConfig = (c: Config) => {
    setConfig(c);
    setForm({
      activo: c.activo,
      tag_id: c.tag_id,
      conv_whatsapp: c.conv_whatsapp,
      conv_whatsapp_activo: c.conv_whatsapp_activo,
      conv_telefono: c.conv_telefono,
      conv_telefono_activo: c.conv_telefono_activo,
      conv_cotizacion: c.conv_cotizacion,
      conv_cotizacion_activo: c.conv_cotizacion_activo,
      ga4_property_id: c.ga4_property_id,
    });
  };

  useEffect(() => {
    axios.get(`${API}/google/`, { headers })
      .then(r => {
        aplicarConfig(r.data);
        if (r.data.diagnostico?.estadisticas_listas) setPestana('estadisticas');
      })
      .catch(() => mostrarAviso('error', '❌ No se pudo cargar la configuración'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarStats = (d: number, refrescar = false) => {
    setCargandoStats(true);
    axios.get(`${API}/google/estadisticas`, { headers, params: { dias: d, refrescar } })
      .then(r => setStats(r.data))
      .catch(() => setStats({ listo: false, error: 'No se pudo consultar el servidor.' }))
      .finally(() => setCargandoStats(false));
  };

  useEffect(() => {
    if (pestana === 'estadisticas') cargarStats(dias);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pestana, dias]);

  const mostrarAviso = (tipo: 'ok' | 'error', texto: string) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 6000);
  };

  const cambiar = <K extends keyof Formulario>(campo: K, valor: Formulario[K]) => {
    setForm(f => ({ ...f, [campo]: valor }));
    setErrores(e => {
      const resto = { ...e };
      delete resto[campo as string];
      return resto;
    });
  };

  const guardar = async () => {
    setGuardando(true);
    setErrores({});
    try {
      const r = await axios.put(`${API}/google/`, form, { headers });
      aplicarConfig(r.data);
      setStats(null);
      mostrarAviso('ok', r.data.activo ? '✅ Guardado. La medición está activa en el sitio' : '✅ Guardado (la medición está apagada)');
    } catch (e: any) {
      const detalle = e?.response?.data?.detail;
      if (detalle && typeof detalle === 'object' && !Array.isArray(detalle) && detalle.errores) {
        setErrores(detalle.errores);
        mostrarAviso('error', `⚠️ ${detalle.mensaje}`);
      } else {
        mostrarAviso('error', typeof detalle === 'string' ? detalle : '❌ No se pudo guardar');
      }
    } finally {
      setGuardando(false);
    }
  };

  const copiar = (texto: string) => {
    navigator.clipboard?.writeText(texto).then(
      () => mostrarAviso('ok', '📋 Copiado'),
      () => mostrarAviso('error', 'No se pudo copiar, selecciónalo a mano')
    );
  };

  const diag = config?.diagnostico;
  const convActivas = CONVERSIONES.filter(c => form[`conv_${c.tipo}` as keyof Formulario] && form[`conv_${c.tipo}_activo` as keyof Formulario]).length;
  const sinGuardar = config !== null && (Object.keys(form) as (keyof Formulario)[]).some(k => form[k] !== config[k]);

  const input = (campo: keyof Formulario, placeholder: string) => (
    <>
      <input
        value={String(form[campo])}
        onChange={e => cambiar(campo, e.target.value as never)}
        placeholder={placeholder}
        spellCheck={false}
        className={`w-full border rounded-lg px-4 py-2.5 text-sm font-mono focus:outline-none ${errores[campo] ? 'border-red-400' : 'border-gray-200 focus:border-azul'}`}
      />
      {errores[campo] && <p className="text-xs text-red-500 font-bold mt-1">{errores[campo]}</p>}
    </>
  );

  // ---------- render de estadisticas ----------

  const renderEstadisticas = () => {
    if (cargandoStats && !stats) return <div className="text-center text-gray-400 py-12">⏳ Consultando Google Analytics...</div>;
    if (!stats) return null;
    if (!stats.listo) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-sm text-yellow-800">
          <p className="font-bold mb-1">Las estadísticas todavía no están disponibles</p>
          <p>{stats.error}</p>
          <button onClick={() => setPestana('configuracion')} className="mt-3 underline font-bold">Ir a configuración</button>
        </div>
      );
    }
    const t = stats.totales!;
    const c = stats.conversiones!;
    const tasa = t.sesiones > 0 ? ((c.whatsapp + c.telefono) / t.sesiones) * 100 : 0;
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi titulo="Visitantes" valor={numero(t.usuarios)} />
          <Kpi titulo="Visitas" valor={numero(t.sesiones)} detalle={`${numero(t.vistas)} páginas vistas`} />
          <Kpi titulo="Tiempo promedio" valor={duracion(t.duracion_promedio_seg)} detalle="por visita" />
          <Kpi titulo="Contactos" valor={numero(c.whatsapp + c.telefono)} detalle={`${tasa.toFixed(1)}% de las visitas`} />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {CONVERSIONES.map(cv => (
            <div key={cv.tipo} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
              <div className="text-3xl">{cv.icono}</div>
              <div>
                <div className="text-gray-500 text-xs font-medium">{cv.titulo}</div>
                <div className="text-2xl font-bold text-azul">{numero(c[cv.tipo])}</div>
              </div>
            </div>
          ))}
        </div>

        <GraficoDiario datos={stats.diario || []} />

        <div className="grid md:grid-cols-2 gap-6">
          <Barras titulo="¿De dónde llegan?" filas={(stats.canales || []).map(x => ({ nombre: traducir(CANALES, x.nombre), valor: x.sesiones }))} />
          <Barras titulo="Páginas más vistas" filas={(stats.paginas || []).map(x => ({ nombre: PAGINAS[x.ruta] ? `${PAGINAS[x.ruta]} (${x.ruta})` : x.ruta, valor: x.vistas }))} />
          <Barras titulo="Dispositivos" filas={(stats.dispositivos || []).map(x => ({ nombre: traducir(DISPOSITIVOS, x.nombre), valor: x.usuarios }))} />
          <Barras titulo="Ciudades" filas={(stats.ciudades || []).map(x => ({ nombre: traducir({}, x.nombre), valor: x.usuarios }))} />
        </div>

        <p className="text-xs text-gray-400 text-center">
          Datos de Google Analytics · consultado {fecha(stats.generado_en)} · los datos de hoy pueden tardar unas horas en aparecer
        </p>
      </div>
    );
  };

  // ---------- render de configuracion ----------

  const renderConfiguracion = () => (
    <div className="space-y-6">
      {/* Etiqueta */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex justify-between items-start gap-4 mb-3">
          <div>
            <h2 className="font-bold text-azul">🏷️ Etiqueta de Google</h2>
            <p className="text-gray-500 text-sm">Conecta el sitio con Google Analytics y Google Ads.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500">{form.activo ? 'Activa' : 'Apagada'}</span>
            <Interruptor activo={form.activo} onChange={v => cambiar('activo', v)} etiqueta="Activar medición" />
          </div>
        </div>
        {input('tag_id', 'G-XXXXXXXXXX')}
        <p className="text-xs text-gray-400 mt-2">
          En Google Ads o Analytics aparece como <b>"ID de etiqueta"</b> o <b>"ID de medición"</b> y empieza con <b>G-</b>.
          Con el interruptor apagado, el sitio no carga nada de Google.
        </p>
      </div>

      {/* Conversiones */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="font-bold text-azul mb-1">🎯 Conversiones de Google Ads</h2>
        <p className="text-gray-500 text-sm mb-4">Le dicen a Google Ads qué es un buen resultado, para que la campaña busque más clientes así.</p>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-gray-600 mb-5 space-y-1">
          <p>1️⃣ En Google Ads: <b>Objetivos → Conversiones → Nueva acción de conversión → Sitio web</b>.</p>
          <p>2️⃣ Elige configurarla <b>manualmente con código</b>, categoría <b>Contacto</b>.</p>
          <p>3️⃣ En el fragmento de evento busca <code className="bg-white px-1 rounded">send_to</code> y copia solo lo que está entre comillas, por ejemplo <code className="bg-white px-1 rounded">AW-123456789/AbCdEf</code>.</p>
          <p className="text-gray-500">Aunque no llenes estos campos, los clics igual se cuentan en las estadísticas.</p>
        </div>

        <div className="space-y-5">
          {CONVERSIONES.map(cv => {
            const campo = `conv_${cv.tipo}` as keyof Formulario;
            const campoActivo = `conv_${cv.tipo}_activo` as keyof Formulario;
            return (
              <div key={cv.tipo}>
                <div className="flex justify-between items-center gap-4 mb-2">
                  <div>
                    <div className="text-sm font-bold text-gray-700">{cv.icono} {cv.titulo}</div>
                    <div className="text-xs text-gray-400">{cv.ayuda}</div>
                  </div>
                  <Interruptor activo={Boolean(form[campoActivo])} onChange={v => cambiar(campoActivo, v as never)} etiqueta={`Activar ${cv.titulo}`} />
                </div>
                {input(campo, 'AW-123456789/AbCdEfGhIj')}
              </div>
            );
          })}
        </div>
      </div>

      {/* GA4 */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="font-bold text-azul mb-1">📈 Estadísticas en este panel</h2>
        <p className="text-gray-500 text-sm mb-4">Para ver visitas y contactos aquí mismo, sin entrar a Google.</p>

        <label className="text-sm font-bold text-gray-700 block mb-2">ID de la propiedad de Google Analytics</label>
        {input('ga4_property_id', '123456789')}
        <p className="text-xs text-gray-400 mt-2 mb-4">
          Es un <b>número</b>, no el código G-. Está en analytics.google.com → <b>Administrar → Detalles de la propiedad</b>.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          <Estado ok={!!diag?.libreria_ga4} texto="Servidor preparado" />
          <Estado ok={!!diag?.credenciales} texto="Credenciales cargadas" />
          <Estado ok={!!diag?.propiedad} texto="Propiedad guardada" />
        </div>

        {diag?.correo_cuenta_servicio && (
          <div className="bg-gray-50 rounded-xl p-4 text-sm">
            <p className="text-gray-600 mb-2">
              En Google Analytics, entra a <b>Administrar → Acceso a la propiedad</b> y agrega este correo con el rol <b>Lector</b>:
            </p>
            <div className="flex gap-2 items-center">
              <code className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs break-all">{diag.correo_cuenta_servicio}</code>
              <button onClick={() => copiar(diag.correo_cuenta_servicio!)} className="shrink-0 border border-azul text-azul rounded-lg px-3 py-2 text-xs font-bold hover:bg-blue-50">
                📋 Copiar
              </button>
            </div>
          </div>
        )}
        {diag && !diag.credenciales && (
          <p className="text-xs text-gray-400">
            Las credenciales las instala el técnico directamente en el servidor (por seguridad no se suben desde aquí).
          </p>
        )}
      </div>

      <div>
        <button
          onClick={guardar}
          disabled={guardando}
          className="w-full bg-azul text-white py-3 rounded-xl font-bold hover:bg-azul-dark transition-colors disabled:opacity-50"
        >
          {guardando ? '⏳ Guardando...' : '💾 Guardar configuración'}
        </button>
        <p className="text-xs text-gray-400 mt-2 text-center">
          Última modificación: {fecha(config?.actualizado_en)}
          {sinGuardar && <span className="text-orange-500 font-bold"> · Hay cambios sin guardar</span>}
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-bold text-2xl text-azul">Google / Medición</h1>
          <p className="text-gray-500 text-sm">Visitas, contactos y conexión con Google Ads</p>
        </div>
        {aviso && (
          <div className={`px-4 py-2 rounded-xl text-sm font-bold ${aviso.tipo === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {aviso.texto}
          </div>
        )}
      </div>

      {config && (
        <div className="flex flex-wrap gap-2 mb-6">
          <Estado ok={config.diagnostico.etiqueta_activa} texto={config.diagnostico.etiqueta_activa ? 'Etiqueta activa en el sitio' : 'Etiqueta apagada'} />
          <Estado ok={convActivas > 0} texto={`Conversiones de Ads: ${convActivas} de 3`} />
          <Estado ok={config.diagnostico.estadisticas_listas} texto={config.diagnostico.estadisticas_listas ? 'Estadísticas conectadas' : 'Estadísticas pendientes'} />
        </div>
      )}

      <div className="flex justify-between items-center gap-3 mb-6 flex-wrap">
        <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
          {(['estadisticas', 'configuracion'] as const).map(p => (
            <button
              key={p}
              onClick={() => setPestana(p)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${pestana === p ? 'bg-azul text-white' : 'text-gray-500 hover:text-azul'}`}
            >
              {p === 'estadisticas' ? '📊 Estadísticas' : '⚙️ Configuración'}
            </button>
          ))}
        </div>

        {pestana === 'estadisticas' && (
          <div className="flex items-center gap-2">
            <select
              value={dias}
              onChange={e => setDias(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-azul"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={28}>Últimos 28 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <button
              onClick={() => cargarStats(dias, true)}
              disabled={cargandoStats}
              className="border border-azul text-azul rounded-lg px-3 py-2 text-sm font-bold hover:bg-blue-50 disabled:opacity-50"
            >
              {cargandoStats ? '⏳' : '🔄'} Actualizar
            </button>
          </div>
        )}
      </div>

      {pestana === 'estadisticas' ? renderEstadisticas() : renderConfiguracion()}
    </div>
  );
}
